import {
  selectVisibleDeviceTypes,
} from 'features/integrations/state/selectors/integrationSelectors';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import DeviceRow from './components/DeviceRow';
import DeviceRowAccordion from './components/DeviceRowAccordion';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';

/**
 * @param {{type: object, name: string, devices?: number[], typeName?: string, depth?: number, onOpenDevice: ()}}
 * @returns
 */
export default function DeviceTypeList({
  type,
  name,
  typeName,
  devices = null,
  depth = 0,
  onOpenDevice,
  widget = false,
  hideHeader = false,
  deviceAccordion = false,
}) {
  const visibleDeviceTypes = useSelector(selectVisibleDeviceTypes);
    const {
      data: connectedDevices = [],
      isLoading: connectedDevicesLoading,
      isError: connectedDevicesError,
    } = useGetConnectedDevicesQuery();
  const [params, setParams] = useSearchParams();

  const generateStringNext = (str) => {
    if (depth < 1) return name + ' - ' + str;
    else return name + ' (' + str + ')';
  };

  const getFilteredDevices = useMemo(() => {
    let filteredDevices = connectedDevices?.filter(
      (device) =>
        device?.device_type?.includes(typeName) && (devices ? devices.includes(device?.id) : true),
    );

    // Only apply device type visibility filter if we're NOT showing devices within a group
    // If 'devices' array is provided, we're rendering devices within a group, so show all
    if (!devices) {
      // Apply device type visibility filter ONLY for standalone device lists
      const effectiveVisibleDeviceTypes = visibleDeviceTypes;

      if (effectiveVisibleDeviceTypes) {
        filteredDevices = filteredDevices?.filter((device) => {
          const deviceTypeMapping = {
            Stage: 'Stage',
            'Manual Road Closure': 'Manual Road Closure',
            'Technology Bridge_Flashing Lights': 'Technology Bridge_Flashing Lights',
            'Technology Bridge_Siren/Giant Voice': 'Technology Bridge_Siren/Giant Voice',
            'Overwatch VMS': 'Overwatch VMS',
          };

          const mappedType = deviceTypeMapping[device.device_type] || device.device_type;
          return effectiveVisibleDeviceTypes[mappedType] !== false;
        });
      }
    }

    // Apply search filter on the already filtered devices
    if (!devices && params.get('search')) {
      const searchTerm = params.get('search').replace(/\s+/g, '').toLowerCase();
      filteredDevices = filteredDevices?.filter(
        (device) =>
          device.alias
            ?.toLowerCase()
            .replace(/\s/g, '')
            .includes(searchTerm.toLowerCase().replace(/\s/g, '')) ||
          device?.name
            ?.toLowerCase()
            .replace(/\s/g, '')
            .includes(searchTerm.toLowerCase().replace(/\s/g, '')) ||
          String(device?.node_id)
            .toLowerCase()
            .includes(searchTerm.toLowerCase().replace(/\s/g, '')),
      );
    }

    // Sort devices by their displayed id (node_id)
    if (Array.isArray(filteredDevices)) {
      filteredDevices = [...filteredDevices].sort((a, b) => a.node_id - b.node_id);
    }

    return filteredDevices;
  }, [connectedDevices, devices, visibleDeviceTypes, params, typeName]);

  if (depth > 100) {
    console.error('Depth limit reached');
    return null;
  }

  if (!type || typeof type !== 'object') {
    console.error('Error in device type');
    return null;
  }

  // Device list goes here for type
  else if (Object.keys(type).length < 1 || Object.keys(type).includes('validDevices')) {
    if (getFilteredDevices?.length > 0)
      return (
        <div>
          {/* Type Name (removes underscores) */}
          {!hideHeader && (
            <p className="text-center border-bottom border-top border-secondary-subtle py-2">
              {name.replace('_', ' ')}
            </p>
          )}

          {/* Device List */}
          {deviceAccordion ? (
            <DeviceRowAccordion
              widget={widget}
              devices={getFilteredDevices}
              onOpenDevice={onOpenDevice}
            />
          ) : (
            <DeviceRow
              widget={widget}
              type={typeName || name}
              devices={getFilteredDevices}
              onOpenDevice={onOpenDevice}
            />
          )}
        </div>
      );
  }

  // Call DeviceTypeList with child devices
  else
    return Object.keys(type).map((key) => (
      <DeviceTypeList
        key={key}
        widget={widget}
        type={type[key]}
        name={generateStringNext(key)}
        typeName={key}
        depth={depth + 1}
        devices={devices}
        onOpenDevice={onOpenDevice}
        visibleDeviceTypes={visibleDeviceTypes} // Pass it down to child components
        deviceAccordion={deviceAccordion}
      />
    ));
}
